import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import Chart from 'react-apexcharts';

const MONTHS_RU = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

const toNum = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const ClientAnalyticsChartApex = ({
    monthTurnover = [],
    monthAvgCheck = [],
    monthOrders = [],
    height = 320,
}) => {
    const theme = useTheme();

    const hasAnyData = useMemo(() => {
        return [...monthTurnover, ...monthAvgCheck, ...monthOrders].some((v) => toNum(v) > 0);
    }, [monthTurnover, monthAvgCheck, monthOrders]);

    const series = useMemo(() => {
        return [
            {
                name: 'Оборот',
                type: 'column',
                data: MONTHS_RU.map((_, i) => toNum(monthTurnover[i])),
            },
            {
                name: 'Средний чек',
                type: 'line',
                data: MONTHS_RU.map((_, i) => toNum(monthAvgCheck[i])),
            },
            {
                name: 'Заказы',
                type: 'line',
                data: MONTHS_RU.map((_, i) => toNum(monthOrders[i])),
            },
        ];
    }, [monthTurnover, monthAvgCheck, monthOrders]);

    const options = useMemo(() => {
        return {
            chart: {
                stacked: false,
                toolbar: { show: false },
                zoom: { enabled: false },
                fontFamily: 'inherit',
            },
            stroke: {
                width: [0, 3, 2],
                curve: 'smooth',
            },
            plotOptions: {
                bar: {
                    columnWidth: '42%',
                    borderRadius: 8,
                },
            },
            dataLabels: {
                enabled: false,
            },
            colors: [
                theme.palette.primary.main,
                theme.palette.secondary.main,
                theme.palette.info ? theme.palette.info.main : '#43a047',
            ],
            xaxis: {
                categories: MONTHS_RU,
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: {
                    style: { colors: theme.palette.text.secondary },
                },
            },
            yaxis: [
                {
                    title: { text: 'Оборот, ₽' },
                    labels: {
                        style: { colors: theme.palette.text.secondary },
                        formatter: (val) => {
                            const n = toNum(val);
                            if (n >= 1000000) return `${Math.round(n / 100000) / 10}м`;
                            if (n >= 1000) return `${Math.round(n / 100) / 10}к`;
                            return `${Math.round(n)}`;
                        },
                    },
                },
                {
                    opposite: true,
                    title: { text: 'Средний чек, ₽' },
                    labels: {
                        style: { colors: theme.palette.text.secondary },
                        formatter: (val) => `${Math.round(toNum(val))}`,
                    },
                },
                {
                    opposite: true,
                    show: false,
                    min: 0,
                },
            ],
            tooltip: {
                shared: true,
                intersect: false,
                theme: theme.palette.type === 'dark' ? 'dark' : 'light',
                y: [
                    {
                        formatter: (val) => `${Math.round(toNum(val)).toLocaleString('ru-RU')} ₽`,
                    },
                    {
                        formatter: (val) => `${Math.round(toNum(val)).toLocaleString('ru-RU')} ₽`,
                    },
                    {
                        formatter: (val) => `${Math.round(toNum(val)).toLocaleString('ru-RU')} шт`,
                    },
                ],
            },
            legend: {
                position: 'top',
                horizontalAlign: 'left',
                labels: { colors: theme.palette.text.primary },
            },
            grid: {
                borderColor: theme.palette.divider,
                strokeDashArray: 4,
            },
        };
    }, [theme]);

    if (!series.length) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" height={height}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (!hasAnyData) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" height={height}>
                <Typography variant="body2" color="textSecondary">
                    Нет данных для графика
                </Typography>
            </Box>
        );
    }

    return <Chart options={options} series={series} type="line" height={height} />;
};

export default ClientAnalyticsChartApex;

